// TagInspectorPopup.jsx — Tag Inspector: live value + 1h chart + history table
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { X, Search } from 'lucide-react';
import questdbService from '../../../services/questdb';

// ── helpers ──────────────────────────────────────────────────────────────────
const rawToMs = (raw) => {
  if (typeof raw === 'number' || /^\d+$/.test(String(raw).trim())) {
    const n = Number(raw);
    return n > 1e15 ? Math.floor(n / 1000) : n > 1e12 ? n : n * 1000;
  }
  let s = String(raw).trim().replace(' ', 'T').replace(/(\.\d{3})\d*/, '$1');
  if (!s.endsWith('Z') && !s.includes('+') && !/[+-]\d\d:\d\d$/.test(s)) s += 'Z';
  return new Date(s).getTime();
};
const fmtTime  = (ms) => new Intl.DateTimeFormat('en-US', { month:'short', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false, timeZone:'UTC' }).format(new Date(ms));
const fmtShort = (ms) => new Intl.DateTimeFormat('en-US', { hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false, timeZone:'UTC' }).format(new Date(ms));

const formatValue = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return String(v);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(4);
};
// ─────────────────────────────────────────────────────────────────────────────

export default function TagInspectorPopup({
  comp, allTags, tagValues, position, onClose, darkMode, plantId
}) {
  const [selectedTag, setSelectedTag] = useState(allTags[0] || '');
  const [historyData, setHistoryData] = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [pos,         setPos]         = useState({ x: position.x, y: position.y });

  const dragging = useRef(false);
  const dragRef  = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !dragRef.current) return;
      setPos({ x: Math.max(0, dragRef.current.px + e.clientX - dragRef.current.mx),
               y: Math.max(0, dragRef.current.py + e.clientY - dragRef.current.my) });
    };
    const up = () => { dragging.current = false; };
    const onFs = () => setPos(prev => ({
      x: Math.min(Math.max(prev.x, 10), window.innerWidth  - 410),
      y: Math.min(Math.max(prev.y, 10), window.innerHeight -  60),
    }));
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup',   up);
    document.addEventListener('fullscreenchange',       onFs);
    document.addEventListener('webkitfullscreenchange', onFs);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup',   up);
      document.removeEventListener('fullscreenchange',       onFs);
      document.removeEventListener('webkitfullscreenchange', onFs);
    };
  }, []);

  const onDragStart = (e) => {
    if (e.target.closest('button') || e.target.closest('select')) return;
    e.preventDefault();
    dragging.current = true;
    dragRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  };

  useEffect(() => {
    if (!selectedTag) { setHistoryData([]); return; }
    let cancelled = false;
    const fetchHistory = async () => {
      setLoading(true);
      try {
        const latestRes = await questdbService.query(
          `SELECT cast(MAX(timestamp) as long) as latest_us FROM scada_wide`, plantId
        );
        const latestUs = latestRes?.[0]?.latest_us;
        if (!latestUs) { setHistoryData([]); setLoading(false); return; }
        const latestIso = new Date(rawToMs(latestUs)).toISOString();

        const sql = `SELECT cast(timestamp as long) as ts_us, ${selectedTag}
          FROM scada_wide
          WHERE timestamp >= dateadd('m', -60, '${latestIso}') AND timestamp <= '${latestIso}'
          ORDER BY timestamp ASC LIMIT 500`;
        const result = await questdbService.query(sql, plantId);
        if (cancelled) return;
        setHistoryData(result.map(row => ({
          time:  rawToMs(row.ts_us),
          value: row[selectedTag] !== null && row[selectedTag] !== undefined
                   ? parseFloat(row[selectedTag]) : null,
        })));
      } catch (err) {
        console.error('TagInspector fetch error:', err);
        setHistoryData([]);
      }
      setLoading(false);
    };
    fetchHistory();
    const iv = setInterval(fetchHistory, 5000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [selectedTag, plantId]);

  const t = darkMode ? {
    bg: '#0f1117', card: '#1a1d29', text: '#e5e7eb', sub: '#9ca3af',
    border: '#374151', accent: '#667eea', grid: '#1f2937', input: '#0f1117',
  } : {
    bg: '#ffffff', card: '#f9fafb', text: '#111827', sub: '#6b7280',
    border: '#e5e7eb', accent: '#667eea', grid: '#f3f4f6', input: '#ffffff',
  };

  const liveValue  = selectedTag && tagValues[selectedTag] !== undefined ? tagValues[selectedTag] : null;
  const tableRows  = [...historyData].reverse();

  const popup = (
    <div style={{
      position: 'fixed', left: pos.x, top: pos.y, width: '400px',
      height: '520px',
      background: t.bg, border: `1px solid ${t.border}`,
      borderRadius: '12px', boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
      zIndex: 99999, display: 'flex', flexDirection: 'column',
      overflow: 'hidden',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
    }}>

      {/* ── Header ── */}
      <div onMouseDown={onDragStart}
        style={{ cursor: 'grab', userSelect: 'none', padding: '10px 12px',
          borderBottom: `1px solid ${t.border}`, flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: '8px' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Search size={14} color={t.accent} />
          <span style={{ fontSize: '13px', fontWeight: '700', color: t.text, flex: 1,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {comp.label || comp.type || 'Tag Inspector'}
          </span>
          {loading && <span style={{ fontSize: '9px', color: t.sub, flexShrink: 0 }}>updating…</span>}
          <button onClick={onClose} style={{
            width: '26px', height: '26px', borderRadius: '6px',
            border: `1px solid rgba(255,255,255,0.2)`,
            background: 'rgba(255,255,255,0.08)',
            color: '#e5e7eb', cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 0, fontSize: '16px', fontWeight: '300', lineHeight: 1,
          }}>
            ×
          </button>
        </div>

        <select
          value={selectedTag}
          onChange={e => setSelectedTag(e.target.value)}
          style={{
            width: '100%', padding: '6px 10px',
            background: t.card, border: `1px solid ${t.border}`,
            borderRadius: '6px', color: t.text, fontSize: '12px',
            cursor: 'pointer', outline: 'none', fontFamily: 'monospace',
          }}
        >
          {allTags.length === 0 && <option value="">No tags configured</option>}
          {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
        </select>
      </div>

      {selectedTag ? (
        <>
          {/* ── Live Value Row ── */}
          <div style={{
            padding: '10px 14px', borderBottom: `1px solid ${t.border}`,
            display: 'flex', alignItems: 'center', gap: '12px', flexShrink: 0,
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '9px', color: t.sub, fontWeight: '600',
                letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
                Live Value
              </div>
              <div style={{
                fontSize: '22px', fontWeight: '800', letterSpacing: '-0.3px', lineHeight: 1.1,
                color: t.accent, fontVariantNumeric: 'tabular-nums',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {formatValue(liveValue)}
              </div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '9px', color: t.sub, fontWeight: '600',
                letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '2px' }}>
                Tag
              </div>
              <div style={{ fontSize: '11px', fontWeight: '600', color: t.text,
                fontFamily: 'monospace', maxWidth: '140px',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {selectedTag}
              </div>
              <div style={{ fontSize: '9px', color: t.sub, marginTop: '1px' }}>
                {historyData.length} records · 1h
              </div>
            </div>
          </div>

          {/* ── Chart ── */}
          <div style={{ height: '180px', padding: '6px 4px 2px',
            borderBottom: `1px solid ${t.border}`, flexShrink: 0 }}>
            {historyData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={historyData} margin={{ top: 4, right: 10, left: -16, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={t.grid} />
                  <XAxis dataKey="time" type="number" domain={['dataMin', 'dataMax']}
                    tickFormatter={fmtShort} tick={{ fontSize: 9, fill: t.sub }} />
                  <YAxis tick={{ fontSize: 9, fill: t.sub }} width={44} />
                  <Tooltip
                    contentStyle={{ background: t.card, border: `1px solid ${t.border}`,
                      borderRadius: '6px', fontSize: '11px', color: t.text, padding: '6px 10px' }}
                    labelFormatter={fmtTime}
                    formatter={(v) => [v !== null ? v : '—', selectedTag]}
                  />
                  <Line type="monotone" dataKey="value" stroke={t.accent}
                    strokeWidth={2} dot={false} activeDot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
                height: '100%', color: t.sub, fontSize: '12px' }}>
                {loading ? 'Loading chart…' : 'No data in last 1 hour'}
              </div>
            )}
          </div>

          {/* ── History Table (scrollable) ── */}
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            <div style={{
              position: 'sticky', top: 0, background: t.card, zIndex: 1,
              borderBottom: `1px solid ${t.border}`, padding: '5px 14px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <span style={{ fontSize: '9px', fontWeight: '700', color: t.sub,
                textTransform: 'uppercase', letterSpacing: '0.5px' }}>History (1h)</span>
              <span style={{ fontSize: '9px', color: t.sub }}>{tableRows.length} rows</span>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
              <thead>
                <tr style={{ background: t.card }}>
                  <th style={{ padding: '4px 14px', textAlign: 'left', color: t.sub,
                    fontWeight: '600', fontSize: '9px', borderBottom: `1px solid ${t.border}` }}>
                    Time (UTC)
                  </th>
                  <th style={{ padding: '4px 14px', textAlign: 'right', color: t.sub,
                    fontWeight: '600', fontSize: '9px', borderBottom: `1px solid ${t.border}` }}>
                    Value
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRows.length === 0 && !loading && (
                  <tr>
                    <td colSpan={2} style={{ padding: '16px', textAlign: 'center',
                      color: t.sub, fontSize: '11px' }}>
                      No data available
                    </td>
                  </tr>
                )}
                {tableRows.map((row, i) => (
                  <tr key={i} style={{
                    background: i % 2 === 0 ? 'transparent'
                      : (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'),
                  }}>
                    <td style={{ padding: '3px 14px', color: t.sub,
                      fontFamily: 'monospace', fontSize: '10px',
                      borderBottom: `1px solid ${t.border}18` }}>
                      {fmtTime(row.time)}
                    </td>
                    <td style={{
                      padding: '3px 14px', textAlign: 'right', fontFamily: 'monospace',
                      fontWeight: row.value !== null ? '600' : '400',
                      color: row.value !== null ? t.text : t.sub,
                      borderBottom: `1px solid ${t.border}18`,
                    }}>
                      {row.value !== null ? row.value : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div style={{ padding: '24px', textAlign: 'center', color: t.sub, fontSize: '12px' }}>
          No tags configured for this component.<br />Assign tags in the SCADA Designer.
        </div>
      )}
    </div>
  );

  return createPortal(popup, document.body);
}
